import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { enqueueSnackbar } from '@mint-lib/notifications';
import { usePermissions } from '@mint-lib/profile-helpers';
import styled from '@mint-lib/styled';
import { getRouteApi } from '@mint/routing';

import { Box, Button, Typography } from '@myn/mui';

import {
  useCreateCustomColumn,
  useDeleteCustomColumn,
  useEditCustomColumn,
} from '../../../../hooks/useCustomColumnMutation.js';
import {
  useCreateCustomTechCostColumn,
  useDeleteCustomTechCostColumn,
  useEditCustomTechCostColumn,
} from '../../../../hooks/useCustomTechCostColumnMutation.js';
import type { TechCostColumn } from '../../../../types/buyingPlan.js';
import {
  CustomDimension,
  Dimension,
} from '../../../../types/campaignBuilder.js';
import { HIDDEN_COLUMNS } from '../../EditModal.constants.js';
import { DimensionsGroup as TDimensionsGroup } from '../../EditModal.types.js';
import CustomColumnForm, { CustomColumn } from '../CustomColumnForm/index.js';
import GroupedDimension from '../GroupedDimension/index.js';
import TechCostForm from '../TechCostForm/TechCostForm.jsx';

export type Props = {
  group: TDimensionsGroup;
  currentDimensions: CustomDimension[];
  searchValue?: string;
  onCustomSaveChange: (isActive: boolean) => void;
  handleDimensionAddition: (dimension: Dimension, index: number) => void;
};

const MAX_DIMENSIONS_COUNT = 4;

const routeApi = getRouteApi(
  '/_secured/campaign/$campaignId/planning/buying-plan',
);

const DimensionsGroup = ({
  group,
  currentDimensions,
  searchValue = '',
  onCustomSaveChange,
  handleDimensionAddition,
}: Props) => {
  const { t } = useTranslation('@myn/planning');

  const { permissions } = usePermissions();
  const noTechCostAccess =
    !permissions.adserverCostFeature || !permissions.dpsCostFeature;

  const isAddTechCostDisabled = group.isTechCost && noTechCostAccess;

  const labelRef = useRef<HTMLDivElement | null>(null);

  const createCustomColumn = useCreateCustomColumn();
  const createTechCostColumn = useCreateCustomTechCostColumn();
  const deleteCustomColumn = useDeleteCustomColumn();
  const deleteCustomTechCostColumn = useDeleteCustomTechCostColumn();
  const editCustomColumn = useEditCustomColumn();
  const editCustomTechCostColumn = useEditCustomTechCostColumn();

  const { campaignId: campaignCollectionId } = routeApi.useParams();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [preEditMode, setPreEditMode] = useState(false);
  const [currentDimension, setCurrentDimension] =
    useState<CustomDimension | null>(null);

  const [anchorEl, setAnchorEl] = useState<
    null | HTMLElement | HTMLButtonElement
  >(null);

  const groupLabel =
    group.label === 'Carbon neutral' ? 'Carbon Offset' : group.label;

  const filteredDimension = useMemo(
    () =>
      group.dimensions.filter((dimension) =>
        dimension.name.toLowerCase().includes(searchValue.toLowerCase()),
      ),
    [group, searchValue],
  );

  useEffect(() => {
    setIsExpanded(false);
  }, [searchValue]);

  const handleExpand = useCallback(() => {
    setIsExpanded((state) => !state);
  }, []);

  const handleCancel = useCallback(() => {
    setPreEditMode(false);
    setIsEditMode(false);
    setIsFormOpen(false);
  }, []);

  const handleAddButtonClick = () => {
    if (isFormOpen) {
      return;
    }
    setAnchorEl(labelRef.current);
    setIsEditMode(false);
    setIsFormOpen(true);
  };

  const onSuccessRequest = (reset: () => void) => {
    setIsEditMode(false);
    setIsFormOpen(false);
    reset();
  };

  const handleCustomColumnAddition = useCallback(
    async (data: CustomColumn, onReset: () => void, onFailed: () => void) => {
      if (!data.format || !data.type) {
        return;
      }

      const customField = {
        label: data.name,
        fieldFormat: data.format,
        fieldType: data.type,
        selectValues:
          data.options?.map(({ name, ...rest }) => ({
            ...rest,
            label: name,
          })) ?? [],
        editable: true,
      };

      try {
        await createCustomColumn(customField);

        onSuccessRequest(onReset);
      } catch (error) {
        onFailed();
      }
    },
    [isFormOpen, campaignCollectionId],
  );

  const handleCustomColumnChange = useCallback(
    async (data: CustomColumn, onReset: () => void, onFailed: () => void) => {
      if (!data.format || !data.type || !currentDimension) {
        return;
      }

      const newCustomField = {
        id: currentDimension.value,
        label: data.name,
        fieldFormat: data.format,
        fieldType: data.type,
        selectValues:
          data.options?.map(({ name, ...rest }) => ({
            ...rest,
            label: name,
          })) ?? [],
        editable: true,
      };

      try {
        await editCustomColumn(newCustomField);
        onSuccessRequest(onReset);
      } catch (error) {
        onFailed();
      }
    },
    [currentDimension],
  );

  const handleCustomColumnRemove = useCallback(
    async (onReset: () => void, onFailed: () => void) => {
      if (!currentDimension) {
        return;
      }

      const onSuccess = () => {
        onReset();
        setIsFormOpen(false);
        enqueueSnackbar({
          variant: 'success',
          autoHideDuration: 3000,
          subTitle: t('{{dimensionName}} was deleted successfully.', {
            dimensionName: currentDimension.name,
          }),
          title: t('Success'),
          textLayout: 'inline',
          removable: true,
        });
      };

      try {
        await deleteCustomColumn(currentDimension.value);
        onSuccess();
      } catch (error) {
        onFailed();
        enqueueSnackbar({
          variant: 'error',
          autoHideDuration: 3000,
          subTitle: t('{{dimensionName}} can not be deleted.', {
            dimensionName: currentDimension.name,
          }),
          title: t('Error'),
          textLayout: 'inline',
          removable: true,
        });
      }
    },
    [currentDimension],
  );

  const handleTechCostColumnAddition = useCallback(
    async (
      { name, techCostPercent }: TechCostColumn,
      onReset: () => void,
      onFailed: () => void,
    ) => {
      const payload = {
        label: name,
        autoTechCostPercent:
          typeof techCostPercent === 'object' ? null : String(techCostPercent),
      };
      try {
        await createTechCostColumn(payload);
        onSuccessRequest(onReset);
      } catch (error) {
        onFailed();
      }
    },
    [],
  );

  const handleTechCostColumnChange = useCallback(
    async (data: TechCostColumn, onReset: () => void, onFailed: () => void) => {
      if (!currentDimension) {
        return;
      }

      const payload = {
        id: currentDimension.value,
        label: data.name,
        autoTechCostPercent:
          typeof data.techCostPercent === 'object'
            ? null
            : String(data.techCostPercent),
      };

      try {
        await editCustomTechCostColumn(payload);
        onSuccessRequest(onReset);
      } catch (error) {
        onFailed();
      }
    },
    [currentDimension],
  );

  const handleTechCostColumnRemove = useCallback(
    async (onSuccess: () => void, onFailed: () => void) => {
      if (!currentDimension) {
        return;
      }

      try {
        await deleteCustomTechCostColumn(currentDimension.value);
        onSuccess();
      } catch (error) {
        onFailed();
      }
    },
    [currentDimension],
  );

  const handleEdit = useCallback(
    (dimension: CustomDimension, clickedEditButton: HTMLButtonElement) => {
      setAnchorEl(clickedEditButton);
      setCurrentDimension(dimension);
      setPreEditMode(true);
      setIsEditMode(true);
      setIsFormOpen(true);
    },
    [],
  );

  const customColumnEditableValues = useMemo(
    () =>
      currentDimension
        ? {
            name: currentDimension.name,
            format: currentDimension.format
              ? currentDimension.format.value
              : null,
            type: currentDimension.type ? currentDimension.type.value : null,
            options: currentDimension.selectOptions ?? [],
          }
        : null,
    [currentDimension],
  );

  const techCostEditableValues = useMemo(
    () =>
      currentDimension
        ? {
            name: currentDimension.name,
            techCostPercent: currentDimension.autoTechCostPercent
              ? Number(currentDimension.autoTechCostPercent)
              : null,
            isAutoCalculationEnabled: Boolean(
              currentDimension.autoTechCostPercent,
            ),
          }
        : null,
    [currentDimension],
  );

  if (!filteredDimension.length && !group.isCustom) {
    return null;
  }

  return (
    <Wrapper>
      <LabelWrapper ref={labelRef}>
        <Typography
          variant="overline01"
          color={(theme) => theme.palette.text.secondary}
        >
          {groupLabel}
        </Typography>
        {group.isCustom || group.isTechCost ? (
          <Button
            variant="text"
            size="small"
            endIcon="Add"
            onClick={handleAddButtonClick}
            disabled={isAddTechCostDisabled}
          >
            {t('Add column')}
          </Button>
        ) : null}
      </LabelWrapper>
      {!isFormOpen && !group.dimensions.length ? (
        <Typography
          variant="label01"
          style={{ textTransform: 'unset' }}
          color={(theme) => theme.palette.text.secondary}
        >
          {t('Click "Add column +" to create a custom column')}
        </Typography>
      ) : null}
      {group.isCustom ? (
        <CustomColumnForm
          isOpen={isFormOpen}
          isEditMode={isEditMode}
          preEditMode={preEditMode}
          editableValues={customColumnEditableValues}
          onCancel={handleCancel}
          onSubmit={handleCustomColumnAddition}
          onEdit={handleCustomColumnChange}
          onDelete={handleCustomColumnRemove}
          onCustomSaveChange={onCustomSaveChange}
          anchorEl={anchorEl}
          setPreEditMode={setPreEditMode}
        />
      ) : null}
      {group.isTechCost ? (
        <TechCostForm
          isOpen={isFormOpen}
          isEditMode={isEditMode}
          editableValues={techCostEditableValues}
          onSubmit={handleTechCostColumnAddition}
          onEdit={handleTechCostColumnChange}
          onCancel={handleCancel}
          onDelete={handleTechCostColumnRemove}
          preEditMode={preEditMode}
          setPreEditMode={setPreEditMode}
          anchorEl={anchorEl}
          setIsFormOpen={setIsFormOpen}
          currentDimension={currentDimension}
        />
      ) : null}
      <ListWrapper $isExpanded={isExpanded}>
        {filteredDimension.map((dimension) => (
          <GroupedDimension
            handleDimensionAddition={handleDimensionAddition}
            currentDimensions={currentDimensions}
            key={dimension.value}
            dimension={dimension}
            isCustom={group.isCustom || group.isTechCost}
            onEdit={handleEdit}
            isHidden={HIDDEN_COLUMNS.includes(dimension.value)}
            isUsed={Boolean(
              currentDimensions.find(
                (selectedDimension) =>
                  selectedDimension.value === dimension.value,
              ),
            )}
          />
        ))}
      </ListWrapper>
      {filteredDimension.length > MAX_DIMENSIONS_COUNT ? (
        <ShowMoreWrapper>
          <Button onClick={handleExpand} variant="text" size="small">
            {isExpanded ? t('Show less') : t('Show more')}
          </Button>
        </ShowMoreWrapper>
      ) : null}
    </Wrapper>
  );
};

const Wrapper = styled(Box)`
  &:not(:last-child) {
    margin-bottom: ${({ theme }) => theme.spacing(2)};
  }
`;

const ListWrapper = styled(Box)<{ $isExpanded?: boolean }>`
  display: flex;
  flex-wrap: wrap;
  max-height: ${({ $isExpanded }) => ($isExpanded ? '100%' : '160px')};
  width: 319px;
  overflow: hidden;
`;

const LabelWrapper = styled(Box)`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  column-gap: ${({ theme }) => theme.spacing(2)};
  margin-bottom: ${({ theme }) => theme.spacing(1)};
`;

const ShowMoreWrapper = styled(Box)`
  display: flex;
  justify-content: center;
  align-items: center;
`;

export default memo(DimensionsGroup);
